import { ReactNode } from 'react';
import { usePlatformSettingsStore, selectMaintenance } from '@/store/platform-settings/store';
import MaintenanceOverlay from '@/core/components/maintenance-overlay';

// Mapping of feature keys used in MaintenanceGate to their display names
const FEATURE_DISPLAY_NAMES: Record<string, string> = {
  EXCHANGE: 'Exchange',
  DCA: 'DCA Strategy',
  MANDATE: 'Management Mandate',
  MAIN_ACCOUNT: 'Main Account',
  TREASURY: 'Treasury',
};

interface MaintenanceGateProps {
  feature: string;
  children: ReactNode;
}

/**
 * Hook to check if a feature is under maintenance
 */
export function useMaintenanceStatus(feature: string): {
  isUnderMaintenance: boolean;
  message?: string;
} {
  const maintenance = usePlatformSettingsStore(selectMaintenance);
  const maintenanceInfo = maintenance[feature];

  return {
    isUnderMaintenance: maintenanceInfo?.active ?? false,
    message: maintenanceInfo?.message,
  };
}

/**
 * MaintenanceGate component to conditionally render children or maintenance overlay.
 * 
 * Usage:
 * <MaintenanceGate feature="EXCHANGE">
 *   <ExchangePage />
 * </MaintenanceGate>
 */
export default function MaintenanceGate({ feature, children }: MaintenanceGateProps) {
  const { isUnderMaintenance, message } = useMaintenanceStatus(feature);

  if (isUnderMaintenance) {
    return (
      <MaintenanceOverlay
        featureName={FEATURE_DISPLAY_NAMES[feature] || feature}
        message={message}
      />
    );
  }

  return <>{children}</>;
}